import {NavLink, useLocation} from 'react-router-dom';
import {useEffect, useRef, useState} from 'react';

type Item = { to: string; label: string };

export default function NavDropdown({label, items}: { label: string; items: Item[] }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const {pathname} = useLocation();

    // close when route changes
    useEffect(() => setOpen(false), [pathname]);

    // close on outside click / Esc
    useEffect(() => {
        const onClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onEsc);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onEsc);
        };
    }, []);

    // @ts-ignore
    const isSectionActive = items.some(i => pathname.startsWith(i.to));

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
                className={`inline-flex items-center gap-1 text-white text-lg transition ${
                    isSectionActive ? 'text-primary font-bold' : 'hover:text-primary'
                }`}
            >
                {label}
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={`h-4 w-4 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
                >
                    <path d="m6 9 6 6 6-6"/>
                </svg>
            </button>

            <div
                role="menu"
                className={`absolute right-0 mt-2 w-56 rounded-xl bg-white text-gray-900 shadow-lg ring-1 ring-black/5 z-50
          ${open ? 'opacity-100 scale-100' : 'pointer-events-none opacity-0 scale-95'} transition`}
            >
                <ul className="py-2">
                    {items.map(item => (
                        <li key={item.to}>
                            <NavLink
                                to={item.to}
                                role="menuitem"
                                className='block px-4 py-2 text-md hover:bg-gray-100'
                                onClick={() => setOpen(false)}
                            >
                                {item.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
